import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { API_URL } from '../config';
import OrderImport, { getOrderImportText } from '../components/OrderImport';

function StatusBadge({ status }) {
  const isDelivered = status === 'delivered' || status === 'completed' || status === 'paid';
  const className = isDelivered
    ? 'bg-[#0f2a0f] text-[#4ade80] border-[#166534]'
    : 'bg-surfaceAlt text-muted border-edge';
  return (
    <span className={`border px-3 py-1 rounded-full text-xs capitalize ${className}`}>
      {status}
    </span>
  );
}

function Orders({ token, onAuthError, tenant }) {
  const tenantCurrency = (tenant && tenant.default_currency) || 'EUR';
  const isAdmin = !!(tenant && tenant.role === 'admin');
  const { t, i18n } = useTranslation();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [platformFilter, setPlatformFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [showManualForm, setShowManualForm] = useState(false);
  const [showImport, setShowImport] = useState(false);
  const [manualOrder, setManualOrder] = useState({
    source_platform: '', total_gross: '', shipping_cost: '0', gateway_fee: '0', currency: tenantCurrency, status: 'paid', customer_name: '', customer_email: '',
  });
  const [savingManual, setSavingManual] = useState(false);
  const [products, setProducts] = useState([]);
  const [orderItems, setOrderItems] = useState([]);
  useEffect(() => {
    setManualOrder((v) => ({ ...v, currency: tenantCurrency }));
  }, [tenantCurrency]);
  const [editingId, setEditingId] = useState(null);
  const [editValues, setEditValues] = useState({});
  const [savingEdit, setSavingEdit] = useState(false);
  const [expandedOrderId, setExpandedOrderId] = useState(null);
  const [orderItemsMap, setOrderItemsMap] = useState({});
  const [loadingItems, setLoadingItems] = useState(false);

  useEffect(() => {
    fetchOrders();
    fetchProducts();
    const interval = setInterval(() => fetchOrders({ silent: true }), 15000);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchProducts = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/v1/products`, authHeaders);
      setProducts(response.data.data || []);
    } catch (err) {
      if (err.response && err.response.status === 401) onAuthError();
    }
  };
  const toggleExpand = async (orderId) => {
    if (expandedOrderId === orderId) {
      setExpandedOrderId(null);
      return;
    }
    setExpandedOrderId(orderId);
    if (!orderItemsMap[orderId]) {
      setLoadingItems(true);
      try {
        const response = await axios.get(`${API_URL}/api/v1/orders/${orderId}/items`, authHeaders);
        setOrderItemsMap((prev) => ({ ...prev, [orderId]: response.data.data || [] }));
      } catch (err) {
        if (err.response && err.response.status === 401) onAuthError();
        setOrderItemsMap((prev) => ({ ...prev, [orderId]: [] }));
      } finally {
        setLoadingItems(false);
      }
    }
  };

  const authHeaders = { headers: { Authorization: `Bearer ${token}` } };

  const deleteOrder = async (orderId) => {
    const confirmMsg = isAdmin ? t('orders.deleteConfirm') : t('orders.deleteRequestConfirm');
    if (!window.confirm(confirmMsg)) return;
    try {
      const response = await axios.delete(`${API_URL}/api/v1/orders/${orderId}`, authHeaders);
      if (isAdmin) {
        setOrders((prev) => prev.filter((o) => o.id !== orderId));
      } else {
        window.alert(response.data.message || t('orders.requestSent'));
      }
    } catch (err) {
      if (err.response && err.response.status === 401) onAuthError();
      else if (err.response && err.response.data && err.response.data.detail) setError(err.response.data.detail);
      else setError(t('orders.deleteError'));
    }
  };

  const fetchOrders = async ({ silent = false } = {}) => {
    if (!silent) setLoading(true);
    setError('');
    try {
      const response = await axios.get(`${API_URL}/api/v1/orders`, authHeaders);
      const data = response.data.data || [];
      data.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
      setOrders(data);
    } catch (err) {
      if (err.response && err.response.status === 401) {
        onAuthError();
      } else if (!silent) {
        setError(t('orders.loadError'));
      }
    } finally {
      if (!silent) setLoading(false);
    }
  };

  const createManualOrder = async (e) => {
    e.preventDefault();

    const validItems = orderItems.filter((it) => it.product_id);
    if (validItems.length > 0) {
      const calculatedGross = validItems.reduce(
        (acc, it) => acc + (parseFloat(it.quantity || 0) * parseFloat(it.unit_price || 0)),
        0
      );
      const enteredGross = parseFloat(manualOrder.total_gross || 0);
      if (Math.abs(enteredGross - calculatedGross) > 0.01) {
        const confirmed = window.confirm(
          t('orders.grossMismatchConfirm', {
            entered: `${manualOrder.currency} ${enteredGross.toFixed(2)}`,
            calculated: `${manualOrder.currency} ${calculatedGross.toFixed(2)}`,
          })
        );
        if (!confirmed) return;
      }
    }

    setSavingManual(true);
    setError('');
    try {
      const response = await axios.post(
        `${API_URL}/api/v1/orders/manual`,
        {
          ...manualOrder,
          total_gross: parseFloat(manualOrder.total_gross),
          shipping_cost: parseFloat(manualOrder.shipping_cost || 0),
          gateway_fee: parseFloat(manualOrder.gateway_fee || 0),
          items: orderItems
            .filter((it) => it.product_id)
            .map((it) => ({
              product_id: it.product_id,
              quantity: parseInt(it.quantity || 1, 10),
              unit_price: parseFloat(it.unit_price || 0),
            })),
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setOrders((prev) => [response.data.data, ...prev]);
      setShowManualForm(false);
      setManualOrder({ source_platform: '', total_gross: '', shipping_cost: '0', gateway_fee: '0', currency: tenantCurrency, status: 'paid', customer_name: '', customer_email: '' });
      setOrderItems([]);
      fetchProducts();
    } catch (err) {
      if (err.response && err.response.status === 401) onAuthError();
      else setError(t('orders.saveError'));
    } finally {
      setSavingManual(false);
    }
  };

  const startEdit = (order) => {
    setEditingId(order.id);
    setEditValues({
      total_gross: order.total_gross,
      shipping_cost: order.shipping_cost ?? 0,
      gateway_fee: order.gateway_fee ?? 0,
      currency: order.currency,
      status: order.status,
      customer_name: order.customer_name || '',
      customer_email: order.customer_email || '',
    });
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditValues({});
  };

  const saveEdit = async (orderId) => {
    setSavingEdit(true);
    setError('');
    try {
      const response = await axios.patch(
        `${API_URL}/api/v1/orders/${orderId}`,
        {
          ...editValues,
          total_gross: parseFloat(editValues.total_gross),
          shipping_cost: parseFloat(editValues.shipping_cost || 0),
          gateway_fee: parseFloat(editValues.gateway_fee || 0),
        },
        authHeaders
      );
      setOrders((prev) => prev.map((o) => (o.id === orderId ? response.data.data : o)));
      setEditingId(null);
      setEditValues({});
    } catch (err) {
      if (err.response && err.response.status === 401) onAuthError();
      else setError(t('orders.saveError'));
    } finally {
      setSavingEdit(false);
    }
  };
  const addItem = () => {
    setOrderItems((prev) => [...prev, { product_id: '', quantity: 1, unit_price: 0 }]);
  };

  const removeItem = (index) => {
    setOrderItems((prev) => {
      const next = prev.filter((_, i) => i !== index);
      recalcGross(next);
      return next;
    });
  };

  const updateItem = (index, field, value) => {
    setOrderItems((prev) => {
      const next = prev.map((it, i) => {
        if (i !== index) return it;
        const updated = { ...it, [field]: value };
        if (field === 'product_id') {
          const product = products.find((p) => p.id === value);
          updated.unit_price = product && product.sale_price != null ? product.sale_price : 0;
        }
        return updated;
      });
      recalcGross(next);
      return next;
    });
  };

  const recalcGross = (items) => {
    const sum = items.reduce((acc, it) => acc + (parseFloat(it.quantity || 0) * parseFloat(it.unit_price || 0)), 0);
    setManualOrder((v) => ({ ...v, total_gross: sum.toFixed(2) }));
  };
  const platforms = useMemo(
    () => ['all', ...new Set(orders.map((o) => o.source_platform))],
    [orders]
  );
  const statuses = useMemo(
    () => ['all', ...new Set(orders.map((o) => o.status))],
    [orders]
  );

  const filtered = orders.filter((o) => {
    if (platformFilter !== 'all' && o.source_platform !== platformFilter) return false;
    if (statusFilter !== 'all' && o.status !== statusFilter) return false;
    if (search && !o.platform_order_id.toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  });

  const selectClass =
    'bg-surfaceAlt border border-edge rounded-lg px-3 py-2 text-white text-sm cursor-pointer focus:outline-none';

  const columns = [
    t('orders.colDate'), t('dashboard.colPlatform'), t('dashboard.colOrderId'), t('orders.colCustomer'), t('dashboard.colGross'),
    t('orders.colShipping'), t('orders.colFee'), t('dashboard.colNet'), t('dashboard.colStatus'), '',
  ];

  return (
    <div>
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between mb-6">
        <div>
          <h2 className="text-white text-2xl font-bold">{t('orders.title')}</h2>
          <p className="text-muted text-sm mt-1">{t('orders.subtitle')}</p>
        </div>
        <div className="flex flex-wrap gap-2 self-start sm:self-auto">
          <button
            onClick={() => setShowImport((v) => !v)}
            className="bg-surfaceAlt border border-edge text-white font-bold text-sm px-5 py-2.5 rounded-lg cursor-pointer"
          >
            {showImport ? t('orders.cancel') : getOrderImportText(i18n.language).openButton}
          </button>
          <button
            onClick={() => setShowManualForm((v) => !v)}
            className="bg-accent text-panel font-bold text-sm px-5 py-2.5 rounded-lg cursor-pointer"
          >
            {showManualForm ? t('orders.cancel') : t('orders.addManual')}
          </button>
        </div>
      </div>

      {showImport && (
        <OrderImport
          token={token}
          currency={tenantCurrency}
          onAuthError={onAuthError}
          onClose={() => setShowImport(false)}
          onImported={() => fetchOrders()}
        />
      )}

      {showManualForm && (
        <form onSubmit={createManualOrder} className="bg-surface border border-edge rounded-2xl p-5 mb-6">
          <p className="text-muted text-xs mb-4">
            {t('orders.manualFormHint')}
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
            <div>
              <label className="text-muted text-xs block mb-1.5">{t('orders.platformOrigin')}</label>
              <input
                type="text"
                required
                placeholder={t('orders.platformPlaceholder')}
                value={manualOrder.source_platform}
                onChange={(e) => setManualOrder((v) => ({ ...v, source_platform: e.target.value }))}
                className="w-full bg-surfaceAlt border border-edge rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-accent"
              />
            </div>
            <div>
              <label className="text-muted text-xs block mb-1.5">{t('orders.grossAmount')}</label>
              <input
                type="number"
                step="0.01"
                required
                value={manualOrder.total_gross}
                onChange={(e) => setManualOrder((v) => ({ ...v, total_gross: e.target.value }))}
                className="w-full bg-surfaceAlt border border-edge rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-accent"
              />
            </div>
            <div>
              <label className="text-muted text-xs block mb-1.5">{t('orders.shippingExtra')}</label>
              <input
                type="number"
                step="0.01"
                value={manualOrder.shipping_cost}
                onChange={(e) => setManualOrder((v) => ({ ...v, shipping_cost: e.target.value }))}
                className="w-full bg-surfaceAlt border border-edge rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-accent"
              />
            </div>
            <div>
              <label className="text-muted text-xs block mb-1.5">{t('orders.gatewayFee')}</label>
              <input
                type="number"
                step="0.01"
                value={manualOrder.gateway_fee}
                onChange={(e) => setManualOrder((v) => ({ ...v, gateway_fee: e.target.value }))}
                className="w-full bg-surfaceAlt border border-edge rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-accent"
              />
            </div>
            <div>
              <label className="text-muted text-xs block mb-1.5">{t('orders.currency')}</label>
              <select
                value={manualOrder.currency}
                onChange={(e) => setManualOrder((v) => ({ ...v, currency: e.target.value }))}
                className="w-full bg-surfaceAlt border border-edge rounded-lg px-3 py-2 text-white text-sm focus:outline-none"
              >
                <option value="EUR">EUR</option>
                <option value="USD">USD</option>
                <option value="CAD">CAD</option>
                <option value="MXN">MXN</option>
                <option value="ARS">ARS</option>
                <option value="CLP">CLP</option>
                <option value="COP">COP</option>
                <option value="HTG">HTG</option>
              </select>
            </div>
            <div>
              <label className="text-muted text-xs block mb-1.5">{t('dashboard.colStatus')}</label>
              <select
                value={manualOrder.status}
                onChange={(e) => setManualOrder((v) => ({ ...v, status: e.target.value }))}
                className="w-full bg-surfaceAlt border border-edge rounded-lg px-3 py-2 text-white text-sm focus:outline-none"
              >
                <option value="paid">{t('orders.statusPaid')}</option>
                <option value="pending">{t('orders.statusPending')}</option>
                <option value="shipped">{t('orders.statusShipped')}</option>
                <option value="cancelled">{t('orders.statusCancelled')}</option>
              </select>
            </div>
            <div>
              <label className="text-muted text-xs block mb-1.5">{t('orders.customerName')}</label>
              <input
                type="text"
                value={manualOrder.customer_name}
                onChange={(e) => setManualOrder((v) => ({ ...v, customer_name: e.target.value }))}
                className="w-full bg-surfaceAlt border border-edge rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-accent"
              />
            </div>
            <div>
              <label className="text-muted text-xs block mb-1.5">{t('orders.customerEmail')}</label>
              <input
                type="email"
                value={manualOrder.customer_email}
                onChange={(e) => setManualOrder((v) => ({ ...v, customer_email: e.target.value }))}
                className="w-full bg-surfaceAlt border border-edge rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-accent"
              />
            </div>
            <div className="flex items-end">
              <button
                type="submit"
                disabled={savingManual}
                className="w-full bg-accent text-panel font-bold text-sm px-4 py-2 rounded-lg cursor-pointer"
              >
                {savingManual ? t('orders.saving') : t('orders.saveOrder')}
              </button>
            </div>
          </div>
          <div className="mt-4">
            <div className="flex items-center justify-between mb-2">
              <label className="text-muted text-xs">{t('orders.itemsSection')}</label>
              <button
                type="button"
                onClick={addItem}
                className="text-accent text-xs px-2 py-1 rounded border border-accent cursor-pointer"
              >
                {t('orders.addItem')}
              </button>
            </div>
            {orderItems.map((item, index) => (
              <React.Fragment key={index}>
              <div className="flex gap-2 mb-2 items-center">
                <select
                  value={item.product_id}
                  onChange={(e) => updateItem(index, 'product_id', e.target.value)}
                  className="flex-1 bg-surfaceAlt border border-edge rounded-lg px-3 py-2 text-white text-sm focus:outline-none"
                >
                  <option value="">{t('orders.selectProduct')}</option>
                  {products.map((p) => (
                    <option key={p.id} value={p.id}>{p.name} ({p.sku})</option>
                  ))}
                </select>
                <input
                  type="number"
                  min="1"
                  value={item.quantity}
                  onChange={(e) => updateItem(index, 'quantity', e.target.value)}
                  placeholder={t('orders.quantity')}
                  className="w-24 bg-surfaceAlt border border-edge rounded-lg px-3 py-2 text-white text-sm focus:outline-none"
                />
                <button
                  type="button"
                  onClick={() => removeItem(index)}
                  className="text-red-400 text-xs px-2 py-2 rounded border border-[#7f1d1d] cursor-pointer"
                >
                  {t('orders.removeItem')}
                </button>
              </div>
              {(() => {
                const selectedProduct = products.find((p) => p.id === item.product_id);
                const qty = parseInt(item.quantity || 0, 10);
                if (selectedProduct && selectedProduct.track_stock !== false && qty > selectedProduct.current_stock) {
                  return (
                    <p className="text-amber-500 text-xs mb-2 -mt-1">
                      {t('orders.stockWarning', { stock: selectedProduct.current_stock })}
                    </p>
                  );
                }
                return null;
              })()}
              </React.Fragment>
            ))}
          </div>        </form>
      )}

      <div className="flex flex-col sm:flex-row gap-3 mb-6">
        <input
          type="text"
          placeholder={t('orders.searchPlaceholder')}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="bg-surfaceAlt border border-edge rounded-lg px-3 py-2 text-white text-sm flex-1 focus:outline-none focus:border-accent"
        />
        <select value={platformFilter} onChange={(e) => setPlatformFilter(e.target.value)} className={selectClass}>
          {platforms.map((p) => (
            <option key={p} value={p}>{p === 'all' ? t('orders.allPlatforms') : p}</option>
          ))}
        </select>
        <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className={selectClass}>
          {statuses.map((s) => (
            <option key={s} value={s}>{s === 'all' ? t('orders.allStatuses') : s}</option>
          ))}
        </select>
      </div>

      {error && (
        <div className="bg-[#2a0f0f] border border-[#7f1d1d] text-red-400 text-sm rounded-lg p-4 mb-6">
          {error}
        </div>
      )}

      <div className="bg-surface border border-edge rounded-2xl p-4 sm:p-6">
        {loading ? (
          <p className="text-muted text-center py-12">{t('orders.loadingOrders')}</p>
        ) : filtered.length === 0 ? (
          <p className="text-muted text-center py-12">
            {orders.length === 0 ? t('orders.noOrdersYet') : t('orders.noMatch')}
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[900px] border-collapse">
              <thead>
                <tr className="border-b border-edge">
                  {columns.map((h) => (
                    <th key={h} className="text-muted text-xs text-left pb-3 uppercase">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filtered.map((order) => {
                  const isEditing = editingId === order.id;
                  return (
                  <React.Fragment key={order.id}>
                  <tr className="border-b border-surfaceAlt">
                    <td className="py-4 text-muted text-sm">
                      {order.created_at ? new Date(order.created_at).toLocaleDateString(i18n.language) : '\u2014'}
                    </td>
                    <td className="py-4 text-white text-sm capitalize">
                      <div className="flex items-center gap-2">
                        <div className="w-2 h-2 rounded-full bg-accent"></div>
                        {order.source_platform}
                      </div>
                    </td>
                    <td className="py-4 text-[#888] text-[13px] font-mono">
                      <button type="button" onClick={() => toggleExpand(order.id)} className="cursor-pointer underline decoration-dotted">
                        {order.platform_order_id}
                      </button>
                    </td>
                    <td className="py-4 text-sm">
                      {isEditing ? (
                        <div className="flex flex-col gap-1">
                          <input
                            type="text"
                            placeholder={t('orders.customerName')}
                            value={editValues.customer_name}
                            onChange={(e) => setEditValues((v) => ({ ...v, customer_name: e.target.value }))}
                            className="w-full bg-surfaceAlt border border-edge rounded px-2 py-1 text-white text-xs focus:outline-none"
                          />
                          <input
                            type="email"
                            placeholder={t('orders.customerEmail')}
                            value={editValues.customer_email}
                            onChange={(e) => setEditValues((v) => ({ ...v, customer_email: e.target.value }))}
                            className="w-full bg-surfaceAlt border border-edge rounded px-2 py-1 text-white text-xs focus:outline-none"
                          />
                        </div>
                      ) : order.customer_name ? (
                        <div>
                          <div className="text-white">{order.customer_name}</div>
                          {order.customer_email && (
                            <div className="text-muted text-xs">{order.customer_email}</div>
                          )}
                        </div>
                      ) : (
                        <span className="text-muted">{'\u2014'}</span>
                      )}
                    </td>
                    <td className="py-4 text-white text-sm font-semibold">
                      {isEditing ? (
                        <input
                          type="number"
                          step="0.01"
                          value={editValues.total_gross}
                          onChange={(e) => setEditValues((v) => ({ ...v, total_gross: e.target.value }))}
                          className="w-24 bg-surfaceAlt border border-edge rounded px-2 py-1 text-white text-xs focus:outline-none"
                        />
                      ) : (
                        <>{order.currency} {order.total_gross}</>
                      )}
                    </td>
                    <td className="py-4 text-muted text-sm">
                      {isEditing ? (
                        <input
                          type="number"
                          step="0.01"
                          value={editValues.shipping_cost}
                          onChange={(e) => setEditValues((v) => ({ ...v, shipping_cost: e.target.value }))}
                          className="w-20 bg-surfaceAlt border border-edge rounded px-2 py-1 text-white text-xs focus:outline-none"
                        />
                      ) : (
                        <>{order.currency} {order.shipping_cost ?? 0}</>
                      )}
                    </td>
                    <td className="py-4 text-muted text-sm">
                      {isEditing ? (
                        <input
                          type="number"
                          step="0.01"
                          value={editValues.gateway_fee}
                          onChange={(e) => setEditValues((v) => ({ ...v, gateway_fee: e.target.value }))}
                          className="w-20 bg-surfaceAlt border border-edge rounded px-2 py-1 text-white text-xs focus:outline-none"
                        />
                      ) : (
                        <>{order.currency} {order.gateway_fee ?? 0}</>
                      )}
                    </td>
                    <td className="py-4 text-accent text-sm font-semibold">
                      {order.currency} {order.total_net}
                      {order.source_platform.toLowerCase().replace(/\s+/g, '') === 'tiktok' && (
                        <span title={t('orders.estimatedNetTooltip')} className="block text-muted text-[10px] font-normal cursor-help underline decoration-dotted">
                          {t('orders.estimatedNet')}
                        </span>
                      )}
                    </td>
                    <td className="py-4">
                      {isEditing ? (
                        <select
                          value={editValues.status}
                          onChange={(e) => setEditValues((v) => ({ ...v, status: e.target.value }))}
                          className="bg-surfaceAlt border border-edge rounded px-2 py-1 text-white text-xs focus:outline-none"
                        >
                          <option value="paid">{t('orders.statusPaid')}</option>
                          <option value="pending">{t('orders.statusPending')}</option>
                          <option value="shipped">{t('orders.statusShipped')}</option>
                          <option value="cancelled">{t('orders.statusCancelled')}</option>
                        </select>
                      ) : (
                        <StatusBadge status={order.status} />
                      )}
                    </td>
                    <td className="py-4 whitespace-nowrap">
                      {isEditing ? (
                        <div className="flex gap-2">
                          <button
                            onClick={() => saveEdit(order.id)}
                            disabled={savingEdit}
                            className="text-accent text-xs px-2 py-1 rounded border border-accent cursor-pointer"
                          >
                            {savingEdit ? t('orders.saving') : t('orders.save')}
                          </button>
                          <button
                            onClick={cancelEdit}
                            className="text-muted text-xs px-2 py-1 rounded border border-edge cursor-pointer"
                          >
                            {t('orders.cancel')}
                          </button>
                        </div>
                      ) : (
                        <button
                          onClick={() => startEdit(order)}
                          className="text-muted text-xs px-2 py-1 rounded border border-edge cursor-pointer"
                        >
                          {t('orders.edit')}
                        </button>
                      )}
                      <button
                        onClick={() => deleteOrder(order.id)}
                        className="text-red-400 text-xs px-2 py-1 rounded border border-[#7f1d1d] cursor-pointer ml-2"
                      >
                        {isAdmin ? t('orders.delete') : t('orders.requestDelete')}
                      </button>
                    </td>
                  </tr>
                  {expandedOrderId === order.id && (
                    <tr className="bg-surfaceAlt/40">
                      <td colSpan={10} className="py-3 px-4">
                        {loadingItems && !orderItemsMap[order.id] ? (
                          <p className="text-muted text-xs">{t('orders.loadingOrders')}</p>
                        ) : (orderItemsMap[order.id] || []).length === 0 ? (
                          <p className="text-muted text-xs">{t('orders.noMatch')}</p>
                        ) : (
                          <table className="w-full text-xs">
                            <tbody>
                              {orderItemsMap[order.id].map((it) => (
                                <tr key={it.id}>
                                  <td className="py-1 text-white">{it.products ? `${it.products.name} (${it.products.sku})` : it.product_id}</td>
                                  <td className="py-1 text-muted">{t('orders.quantity')}: {it.quantity}</td>
                                  <td className="py-1 text-muted">{'\u20AC'}{it.unit_price} / u.</td>
                                  <td className="py-1 text-accent font-semibold">{'\u20AC'}{(it.quantity * it.unit_price).toFixed(2)}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        )}
                        {(orderItemsMap[order.id] || []).length > 0 && (() => {
                          const itemsProfit = orderItemsMap[order.id].reduce(
                            (acc, it) => acc + (it.quantity * (it.unit_price - (it.cost_price ?? 0))),
                            0
                          );
                          const realProfit = itemsProfit - (order.shipping_cost ?? 0) - (order.gateway_fee ?? 0);
                          return (
                            <p className="mt-2 text-xs">
                              <span className="text-muted">{t('orders.realProfit')}: </span>
                              <span className={realProfit >= 0 ? 'text-accent font-semibold' : 'text-red-400 font-semibold'}>
                                {order.currency} {realProfit.toFixed(2)}
                              </span>
                            </p>
                          );
                        })()}
                      </td>
                    </tr>
                  )}
                  </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

export default Orders;